import { currencyFormatter } from "@common/helpers/currency";
import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import { InvoiceScheduleChange } from "@common/schemas/domains/invoice/invoice-schedule-change.schema";
import { ScheduleSourceEditOutcome } from "@common/types/domains/accounting/schedule";
import type { BulkScheduleNoun } from "../bulk/bulkScheduleRows";

export type ScheduleChangeSource = "transaction" | "invoice" | "bill";

export interface ScheduleChangeCopy {
    title: string;
    question: string;
}

/**
 * Delete offers two answers: reverse the entries (the ledger keeps its
 * history) or remove them outright, in which case the audit log is the only
 * record. Shared by the applications table and the schedule pickers.
 */
export const SCHEDULE_DELETE_BODY =
    "Would you like to create reversal journal entries or remove them instead (log accessible via Activity)? This action can't be undone.";
export const SCHEDULE_DELETE_REVERSE_LABEL = "Reverse journal entries";
export const SCHEDULE_DELETE_REMOVE_LABEL = "Remove journal entries";

export function buildScheduleDeleteTitle(scheduleName: string): string {
    return `Remove ${scheduleName}?`;
}

function scheduleNameOrFallback(
    name: string | null | undefined,
    fallback: string,
): string {
    return name == null || name.length === 0 ? fallback : name;
}

/**
 * Removal goes through the lifecycle `delete` verb, which reverses an earned
 * entry in its own month and hard-deletes a future one.
 */
function buildDeletionSentence(options: {
    currentScheduleName: string;
    journalEntryCount: number;
}): string {
    return `${options.currentScheduleName} will be removed, and ${pluralizeWithCount("journal entry", options.journalEntryCount)} will be reversed or deleted.`;
}

export function buildReplaceScheduleCopy(options: {
    source: ScheduleChangeSource;
    currentScheduleName: string | null | undefined;
    newScheduleName: string | null | undefined;
    journalEntryCount: number;
}): ScheduleChangeCopy {
    const currentScheduleName = scheduleNameOrFallback(
        options.currentScheduleName,
        "this schedule",
    );
    const newScheduleName = scheduleNameOrFallback(
        options.newScheduleName,
        "the new schedule",
    );
    return {
        title: `Replace ${currentScheduleName}?`,
        question: `This ${options.source} will move to ${newScheduleName}. ${buildDeletionSentence(
            {
                currentScheduleName,
                journalEntryCount: options.journalEntryCount,
            },
        )}`,
    };
}

export function buildRemoveScheduleCopy(options: {
    currentScheduleName: string | null | undefined;
}): ScheduleChangeCopy {
    return {
        title: buildScheduleDeleteTitle(
            scheduleNameOrFallback(
                options.currentScheduleName,
                "this schedule",
            ),
        ),
        question: SCHEDULE_DELETE_BODY,
    };
}

export function buildBulkReplaceScheduleCopy(options: {
    noun: BulkScheduleNoun;
    replaceCount: number;
    totalCount: number;
    newScheduleName: string;
    skippedWarning?: string;
}): ScheduleChangeCopy {
    const withSkipped = (question: string) =>
        options.skippedWarning
            ? `${question} ${options.skippedWarning}`
            : question;
    if (options.noun === "schedule") {
        const schedules = pluralizeWithCount("schedule", options.replaceCount);
        return {
            title: `Move ${schedules} to ${options.newScheduleName}?`,
            question: withSkipped(
                `${options.replaceCount === 1 ? "Its" : "Their"} current journal entries will be reversed or deleted, and ${options.replaceCount === 1 ? "it" : "each one"} will be re-created on ${options.newScheduleName}.`,
            ),
        };
    }
    const scheduledRows = pluralizeWithCount(
        options.noun,
        options.replaceCount,
    );
    return {
        title: `Replace ${pluralize("schedule", options.replaceCount)} on ${options.replaceCount} of ${pluralizeWithCount(options.noun, options.totalCount)}?`,
        question: withSkipped(
            `${scheduledRows} already ${options.replaceCount === 1 ? "has" : "have"} a schedule. ${options.replaceCount === 1 ? "It" : "They"} will move to ${options.newScheduleName}, and the current journal entries will be reversed or deleted. The rest will be scheduled as new.`,
        ),
    };
}

export function buildReplaceLostCopy(options: {
    currentScheduleName: string | null | undefined;
}): string {
    const currentScheduleName = scheduleNameOrFallback(
        options.currentScheduleName,
        "the current schedule",
    );
    return `Removed ${currentScheduleName}, but couldn't apply the new schedule. Set a schedule again.`;
}

/** Toast after an invoice save carried its schedule along (no confirm in V1). */
export function buildInvoiceScheduleChangeToast(
    change: InvoiceScheduleChange,
): string {
    switch (change.outcome) {
        case ScheduleSourceEditOutcome.changed:
            return `${change.templateName} updated to match the invoice (${currencyFormatter.format(change.previousAmount)} → ${currencyFormatter.format(change.newAmount)})`;
        case ScheduleSourceEditOutcome.removed:
            return `${change.templateName} removed — its invoice lines were deleted`;
    }
}
